// W6: the words on the desk's Health screen. Pure, and loadable without a
// bundler so the Worker's suite, the only one `npm run check` gates on, can load it.
//
// Its one import is `zone.ts`, spelled with its extension and import-free
// itself (C2.1: a date is printed in the current region's zone).
import { hereZone } from './zone.ts'

export interface HealthCounts {
  /** Venue calendars whose last run was clean, and how many there are.
   *  Probe recipes are left out by the caller, as `App.tsx`'s dot does. */
  ok: number
  total: number
  alerts: number
  /** Runs in the window that were not `ok`. */
  badRuns: number
  /** Model spend over the same week the runs came from, in dollars. */
  spendUsd: number
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * The model spend the app can actually read, summed (a3-31).
 *
 * The sentence used to end "of a lifetime under $2" — a literal, true when
 * CLAUDE.md wrote it and not since: repair alone was $1.02 in two weeks, and
 * Ask $2.39 in five days. Two tables record a cost per call and both are
 * readable here: `harvest_runs.model_cost_usd` (a model repairing a page)
 * and `chat_messages.cost_usd` (Ask). The other passes — scoring,
 * translation, tickets, aisles, exhibition prose — total their cost in a
 * run's report and store it nowhere a client can sum, so this is a floor,
 * and the words say "recorded", not "lifetime". `since` is the first
 * recorded spend: the figure's own start, not a claim about before it.
 */
export interface RecordedSpend {
  usd: number
  /** ISO instant of the earliest row with a cost, or null with none. */
  since: string | null
}

export function recordedSpend(rows: { at: string; usd: number | string | null }[]): RecordedSpend {
  let usd = 0
  let since: string | null = null
  for (const r of rows) {
    const n = Number(r.usd) || 0
    if (n <= 0) continue
    usd += n
    if (since === null || r.at < since) since = r.at
  }
  return { usd, since }
}

/** "$3.41 recorded since 20 Sept" — the current region's day (C2.1). */
export function recordedSpendWords(r: RecordedSpend): string | null {
  if (!r.since) return null
  const day = new Date(r.since).toLocaleDateString('en-GB', { timeZone: hereZone(), day: 'numeric', month: 'short' })
  return `$${r.usd.toFixed(2)} recorded since ${day}`
}

/** One sentence of state. Its numbers are the week's: the screen narrows the
 *  14 days it reads to 7 before counting. The recorded total closes it when
 *  it has loaded, and is simply left off while it has not — no figure is
 *  better than a made-up one. */
export function statusSentence(c: HealthCounts, recorded?: RecordedSpend | null): string {
  const alerts = c.alerts === 0
    ? 'No alerts waiting for you.'
    : `${plural(c.alerts, 'alert', 'alerts')} waiting for you.`
  const runs = `${plural(c.badRuns, 'run', 'runs')} this week ${c.badRuns === 1 ? 'was' : 'were'} not clean.`
  const total = recorded ? recordedSpendWords(recorded) : null
  return `${c.ok} of ${c.total} venue calendars read cleanly last time. ${alerts} ${runs} `
    + `$${c.spendUsd.toFixed(2)} spent on models this week${total ? `, ${total}` : ''}.`
}

/** Runs started within the last `days` days of `now`. Dates, not a re-query:
 *  the hook reads 14 days and the week-bound cards narrow it here. */
export function withinDays<T extends { started_at: string }>(runs: T[], days: number, now: number): T[] {
  const from = now - days * 86400000
  return runs.filter(r => new Date(r.started_at).getTime() >= from)
}

/** One slot per local day, oldest first, `days` of them ending today; a day
 *  with no run is a zero slot, not a missing one. */
export function dailySpend(
  runs: { started_at: string; model_cost_usd: number | string | null }[], days: number, now: Date,
): { d: Date; key: string; usd: number; runs: number }[] {
  const today = new Date(now); today.setHours(0, 0, 0, 0)
  const out: { d: Date; key: string; usd: number; runs: number }[] = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i)
    out.push({ d, key: d.toDateString(), usd: 0, runs: 0 })
  }
  for (const r of runs) {
    const slot = out.find(x => x.key === new Date(r.started_at).toDateString())
    if (slot) { slot.usd += Number(r.model_cost_usd) || 0; slot.runs += 1 }
  }
  return out
}

/** What a run's status word means, for the key on "What went wrong". */
export const STATUS: Record<'failed' | 'blocked' | 'repaired', string> = {
  failed: 'the page errored or came back empty',
  blocked: 'the site said stop (429 or 403), and the rule is to stop',
  repaired: 'a model read the broken page instead, which is what costs money',
}

/** What an alert's kind means, for the key on "Is anything broken?". Every
 *  kind `alerts.kind`'s CHECK allows (the Worker's `store.ts` names the same
 *  six); the key had two, so a `repair_failed` row wore a tag nothing above
 *  it explained (UI-audit a3-15). */
export const ALERT_KIND = {
  broken: 'the recipe\u2019s runs keep failing, so its calendar is not being read',
  blocked: 'the site said stop (429 or 403), and the rule is to stop',
  repair_failed: 'a model was paid to repair the page, twice, and could not',
  budget_exhausted: 'the run spent its repair budget before the page was fixed, so it stopped paying',
  fallback_failed: 'the calendar\u2019s own page and its fallback both failed',
  disagreement: 'two sources for the same calendar say different things',
} as const

export type AlertKind = keyof typeof ALERT_KIND

/** The two the key always shows: they are tied to the sentence under it and
 *  to card 3's own words. The rest join the key only while a row wears them. */
const ALWAYS: AlertKind[] = ['broken', 'blocked']

const known = (k: string): k is AlertKind => Object.prototype.hasOwnProperty.call(ALERT_KIND, k)

/** The kinds the key explains: the two above, then every other known kind
 *  among the open alerts, in the order `ALERT_KIND` lists them. */
export function alertKey(present: readonly string[]): AlertKind[] {
  const on = new Set(present)
  return (Object.keys(ALERT_KIND) as AlertKind[]).filter(k => ALWAYS.includes(k) || on.has(k))
}

/** An alert kind as a tag says it: words, not an enum. A kind this file has
 *  never heard of is still humanised rather than printed raw. */
export const alertLabel = (kind: string) => kind.replace(/_/g, ' ')

/** The tag's tone: being told to stop is a warning, everything else is a fault. */
export const alertTone = (kind: string): 'warn' | 'bad' => (kind === 'blocked' ? 'warn' : 'bad')

/** The key's words for a kind, or null for one this file does not know. */
export const alertWords = (kind: string): string | null => (known(kind) ? ALERT_KIND[kind] : null)

/** The same fact under two words: card 1 names the recipe, card 3 names its runs. */
export const BROKEN_IS_FAILED = 'A broken recipe is one whose runs failed, so the same venue can read broken here and failed below.'

/** What a recipe's status means, for "The recipes". */
export const RECIPE_STATUS: Record<'ok' | 'paused', string> = {
  ok: 'its last run came back clean',
  paused: 'switched off on purpose, so it is not read and not counted as broken',
}

/** The line under each card's title: what the card is. */
export const CARD = {
  broken: 'An alert is a recipe (one venue’s calendar) that failed or was told to stop, and stays here until you acknowledge it. Acknowledging does not fix it; it says you have seen it.',
  // "or the nightly scoring ran" was dropped (a3-31): scoring writes no
  // `harvest_runs` row, so no bar here was ever scoring.
  cost: 'Model spend per day for the last 14 days. Reading a page costs nothing; a bar is a night when a broken page was repaired by a model.',
  week: 'Every attempt to read a venue’s page that did not come back clean, newest first.',
  recipes: 'One recipe per calendar the Worker reads nightly. Paused ones are yours to un-pause; a probe is a deliberate test of a failure path.',
  notes: 'What you and anyone you have invited typed in the app’s account sheet; a note from someone else carries the start of their user id. Close one by saying what changed; anything you would notice also gets a release note.',
} as const

/** The cost card's footnote (a3-31): what the recorded figure covers, so a
 *  total that is a floor is not read as the whole. */
export function recordedSpendNote(r: RecordedSpend | null | undefined): string | null {
  const words = r ? recordedSpendWords(r) : null
  if (!words) return null
  return `${words}: page repairs and Ask. Scoring, translation and the other nightly passes are not logged per call, so the whole is higher.`
}
